import { GraduationCap, BookMarked, Users } from 'lucide-react';
import PageHero from '@/components/PageHero';
import { Reveal, SectionHeading } from '@/components/Reveal';
import { PRINCIPAL, FACULTY, ADMIN_STAFF } from '@/data/content';

const initials = (name) => name.split(' ').filter((w) => w.length > 2 || /^[A-Z]/.test(w)).slice(0, 2).map((w) => w[0]).join('');

const Faculty = () => (
  <>
    <PageHero eyebrow="Faculty & Staff" title="Teachers who know every child by name." description="45+ qualified, experienced and caring educators — most with B.Ed. degrees and a decade or more in the classroom." />

    <section className="py-24 sm:py-32" data-testid="principal-profile">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading number="01" eyebrow="Leadership" title="Our Principal" />
        <Reveal delay={0.15}>
          <div className="mt-12 flex flex-col sm:flex-row items-center sm:items-start gap-8 rounded-xl border border-navy/10 bg-white p-8 sm:p-10">
            <img src={PRINCIPAL.photo} alt={PRINCIPAL.name} className="h-44 w-36 rounded-lg object-cover ring-4 ring-gold/40" />
            <div>
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-navy">{PRINCIPAL.name}</h3>
              <p className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-crimson">{PRINCIPAL.designation}</p>
              <p className="mt-1 text-sm text-navy/60">{PRINCIPAL.qualification}</p>
              <p className="mt-5 max-w-2xl text-navy/70 leading-relaxed">“{PRINCIPAL.message[1]}”</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="bg-cream-dark py-24 sm:py-32" data-testid="teachers-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading number="02" eyebrow="Our Teachers" title="The heart of Ideal" description="Sample faculty profiles — the school office can share the complete list during your campus visit." />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {FACULTY.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.06}>
              <div data-testid={`teacher-${i}`} className="group h-full rounded-lg border border-navy/10 bg-white p-7 text-center transition-all hover:-translate-y-1 hover:border-gold">
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-navy font-display text-2xl font-bold text-gold ring-4 ring-gold/30 transition-transform group-hover:scale-105">
                  {initials(t.name)}
                </div>
                <h3 className="mt-5 font-display text-lg font-bold text-navy">{t.name}</h3>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.15em] text-crimson">{t.role}</p>
                <p className="mt-2 flex items-center justify-center gap-1.5 text-sm text-navy/60">
                  <GraduationCap className="h-4 w-4 text-gold" /> {t.qual}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-24 sm:py-32" data-testid="admin-staff-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading number="03" eyebrow="Administrative Staff" title="The team behind the scenes" />
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {ADMIN_STAFF.map((s, i) => (
            <Reveal key={s.name} delay={i * 0.06}>
              <div data-testid={`staff-${i}`} className="flex items-center gap-4 rounded-lg border border-navy/10 bg-white px-5 py-5 transition-all hover:-translate-y-1 hover:border-gold">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-crimson-light text-crimson shrink-0">
                  {s.role.includes('Office') ? <Users className="h-5 w-5" /> : <BookMarked className="h-5 w-5" />}
                </span>
                <div>
                  <p className="font-bold text-navy text-sm">{s.name}</p>
                  <p className="text-xs text-navy/50">{s.role}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </>
);

export default Faculty;
